import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { RecentColorsSchema } from '@/schemas/storage';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pipette } from 'lucide-react';
import { hsbToRgb, rgbToHsb, hexToRgb, rgbToHex, rgbToHsl, hslToRgb } from '@/utils/colorUtils';

// ─── Constants ───────────────────────────────────────────────────────────────

const RECENT_COLORS_KEY = 'terramatrix-recent-colors';
const MAX_RECENT = 8;

const PRESET_COLORS = [
  '#000000', '#FFFFFF', '#6B7280', '#9CA3AF',
  '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#14B8A6', '#3B82F6', '#6366F1', '#A855F7',
  '#EC4899', '#F43F5E', '#1CA9CC', '#2F855A',
  '#1E40AF', '#7C3AED', '#B45309', '#991B1B',
];

// ─── Types ───────────────────────────────────────────────────────────────────

interface ColorPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectColor: (color: string) => void;
  currentColor?: string;
  propertyLabel?: string;
}

interface HSB { h: number; s: number; b: number }
interface RGB { r: number; g: number; b: number }

// ─── Helpers ─────────────────────────────────────────────────────────────────

function parseColor(color: string | undefined): HSB {
  if (!color) return { h: 0, s: 100, b: 100 };
  try {
    const [r, g, b] = hexToRgb(color.startsWith('#') ? color : `#${color}`);
    const [h, s, bVal] = rgbToHsb(r, g, b);
    return { h, s, b: bVal };
  } catch {
    return { h: 0, s: 100, b: 100 };
  }
}

function getRecentColors(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_COLORS_KEY);
    if (!stored) return [];
    const raw: unknown = JSON.parse(stored);
    const result = RecentColorsSchema.safeParse(raw);
    return result.success ? result.data : [];
  } catch {
    return [];
  }
}

function saveRecentColor(color: string) {
  try {
    const recent = getRecentColors().filter(c => c.toUpperCase() !== color.toUpperCase());
    recent.unshift(color.toUpperCase());
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch { /* ignore storage errors */ }
}

const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

// ─── Checkerboard pattern for alpha ──────────────────────────────────────────

const CHECKER_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Crect width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' y='8' width='8' height='8' fill='%23ccc'/%3E%3Crect x='8' width='8' height='8' fill='%23fff'/%3E%3Crect y='8' width='8' height='8' fill='%23fff'/%3E%3C/svg%3E")`;

// ─── Component ───────────────────────────────────────────────────────────────

export const ColorPickerModal: React.FC<ColorPickerModalProps> = ({
  isOpen,
  onClose,
  onSelectColor,
  currentColor,
  propertyLabel,
}) => {
  const [hsb, setHsb] = useState<HSB>(() => parseColor(currentColor));
  const [alpha, setAlpha] = useState(100);
  const [hexInput, setHexInput] = useState('');
  const [recentColors, setRecentColors] = useState<string[]>([]);
  const [inputMode, setInputMode] = useState<'hex' | 'rgb' | 'hsl'>('hex');

  const satBrightRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const alphaRef = useRef<HTMLDivElement>(null);
  const isDraggingSB = useRef(false);
  const isDraggingHue = useRef(false);
  const isDraggingAlpha = useRef(false);

  // Derived colors
  const rgb = useMemo<RGB>(() => {
    const [r, g, b] = hsbToRgb(hsb.h, hsb.s, hsb.b);
    return { r, g, b };
  }, [hsb]);

  const hex = useMemo(() => rgbToHex(rgb.r, rgb.g, rgb.b), [rgb]);

  const hsl = useMemo(() => {
    const [h, s, l] = rgbToHsl(rgb.r, rgb.g, rgb.b);
    return { h, s, l };
  }, [rgb]);

  const outputColor = useMemo(() => {
    if (alpha < 100) {
      return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(alpha / 100).toFixed(2)})`;
    }
    return hex;
  }, [rgb, alpha, hex]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      const parsed = parseColor(currentColor);
      setHsb(parsed);
      setAlpha(100);
      setHexInput(currentColor?.startsWith('#') ? currentColor.toUpperCase() : rgbToHex(...hsbToRgb(parsed.h, parsed.s, parsed.b)));
      setRecentColors(getRecentColors());
    }
  }, [isOpen, currentColor]);

  // Sync hex input when HSB changes (but not when user is typing)
  const hexInputFocused = useRef(false);
  useEffect(() => {
    if (!hexInputFocused.current) {
      setHexInput(hex);
    }
  }, [hex]);

  // ─── Saturation-Brightness Picker ───────────────────────────────────────

  const handleSBInteraction = useCallback((clientX: number, clientY: number) => {
    const el = satBrightRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setHsb(prev => ({ ...prev, s: Math.round(x * 100), b: Math.round((1 - y) * 100) }));
  }, []);

  const handleSBPointerDown = useCallback((e: React.PointerEvent) => {
    isDraggingSB.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    handleSBInteraction(e.clientX, e.clientY);
  }, [handleSBInteraction]);

  const handleSBPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingSB.current) return;
    handleSBInteraction(e.clientX, e.clientY);
  }, [handleSBInteraction]);

  const handleSBPointerUp = useCallback(() => {
    isDraggingSB.current = false;
  }, []);

  // ─── Hue Strip ─────────────────────────────────────────────────────────

  const handleHueInteraction = useCallback((clientY: number) => {
    const el = hueRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = Math.max(0, Math.min(1, (clientY - rect.top) / rect.height));
    setHsb(prev => ({ ...prev, h: Math.round(y * 360) }));
  }, []);

  const handleHuePointerDown = useCallback((e: React.PointerEvent) => {
    isDraggingHue.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    handleHueInteraction(e.clientY);
  }, [handleHueInteraction]);

  const handleHuePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingHue.current) return;
    handleHueInteraction(e.clientY);
  }, [handleHueInteraction]);

  const handleHuePointerUp = useCallback(() => {
    isDraggingHue.current = false;
  }, []);

  // ─── Alpha Slider ──────────────────────────────────────────────────────

  const handleAlphaInteraction = useCallback((clientX: number) => {
    const el = alphaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setAlpha(Math.round(x * 100));
  }, []);

  const handleAlphaPointerDown = useCallback((e: React.PointerEvent) => {
    isDraggingAlpha.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    handleAlphaInteraction(e.clientX);
  }, [handleAlphaInteraction]);

  const handleAlphaPointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingAlpha.current) return;
    handleAlphaInteraction(e.clientX);
  }, [handleAlphaInteraction]);

  const handleAlphaPointerUp = useCallback(() => {
    isDraggingAlpha.current = false;
  }, []);

  // ─── Input Handlers ────────────────────────────────────────────────────

  const handleHexChange = useCallback((value: string) => {
    setHexInput(value);
    const clean = value.startsWith('#') ? value : `#${value}`;
    if (/^#[0-9A-Fa-f]{6}$/.test(clean)) {
      const [r, g, b] = hexToRgb(clean);
      const [h, s, bVal] = rgbToHsb(r, g, b);
      setHsb({ h, s, b: bVal });
    }
  }, []);

  const handleRgbChange = useCallback((channel: 'r' | 'g' | 'b', value: number) => {
    const clamped = Math.max(0, Math.min(255, isNaN(value) ? 0 : value));
    const newRgb = { ...rgb, [channel]: clamped };
    const [h, s, bVal] = rgbToHsb(newRgb.r, newRgb.g, newRgb.b);
    setHsb({ h, s, b: bVal });
  }, [rgb]);

  const handleHslChange = useCallback((channel: 'h' | 's' | 'l', value: number) => {
    const maxVal = channel === 'h' ? 360 : 100;
    const clamped = Math.max(0, Math.min(maxVal, isNaN(value) ? 0 : value));
    const newHsl = { ...hsl, [channel]: clamped };
    const [r, g, b] = hslToRgb(newHsl.h, newHsl.s, newHsl.l);
    const [hNew, sNew, bVal] = rgbToHsb(r, g, b);
    setHsb({ h: hNew, s: sNew, b: bVal });
  }, [hsl]);

  // ─── Eyedropper ────────────────────────────────────────────────────────

  const handleEyeDropper = useCallback(async () => {
    if (!supportsEyeDropper) return;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dropper = new (window as any).EyeDropper();
      const result = await dropper.open();
      const color = result.sRGBHex as string;
      const [r, g, b] = hexToRgb(color);
      const [h, s, bVal] = rgbToHsb(r, g, b);
      setHsb({ h, s, b: bVal });
    } catch {
      // User cancelled or API error — ignore
    }
  }, []);

  // ─── Apply / Swatch Click ──────────────────────────────────────────────

  const handleApply = useCallback(() => {
    saveRecentColor(hex);
    onSelectColor(outputColor);
  }, [hex, outputColor, onSelectColor]);

  const handleSwatchClick = useCallback((color: string) => {
    const [r, g, b] = hexToRgb(color);
    const [h, s, bVal] = rgbToHsb(r, g, b);
    setHsb({ h, s, b: bVal });
    setAlpha(100);
  }, []);

  // ─── Gradient backgrounds ──────────────────────────────────────────────

  const hueColor = useMemo(() => {
    const [r, g, b] = hsbToRgb(hsb.h, 100, 100);
    return rgbToHex(r, g, b);
  }, [hsb.h]);

  const sbGradientStyle = useMemo(() => ({
    background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`,
  }), [hueColor]);

  const hueGradient = 'linear-gradient(to bottom, #FF0000, #FF8000, #FFFF00, #80FF00, #00FF00, #00FF80, #00FFFF, #0080FF, #0000FF, #8000FF, #FF00FF, #FF0080, #FF0000)';

  const alphaGradient = useMemo(() =>
    `linear-gradient(to right, transparent, ${hex})`,
  [hex]);

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent variant="light" className="sm:max-w-[440px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="text-base font-semibold text-gray-900">
            {propertyLabel ? `Pick ${propertyLabel}` : 'Color Picker'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-5 pb-5 space-y-4">
          {/* ── Picker Area ── */}
          <div className="flex gap-3">
            {/* Saturation-Brightness square */}
            <div
              ref={satBrightRef}
              className="relative flex-1 rounded-lg cursor-crosshair overflow-hidden"
              style={{ ...sbGradientStyle, aspectRatio: '1', minHeight: 200 }}
              onPointerDown={handleSBPointerDown}
              onPointerMove={handleSBPointerMove}
              onPointerUp={handleSBPointerUp}
            >
              {/* Selector dot */}
              <div
                className="absolute w-4 h-4 rounded-full border-2 border-white pointer-events-none"
                style={{
                  left: `${hsb.s}%`,
                  top: `${100 - hsb.b}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
                }}
              />
            </div>

            {/* Hue strip */}
            <div
              ref={hueRef}
              className="relative w-5 rounded-lg cursor-pointer overflow-hidden"
              style={{ background: hueGradient, minHeight: 200 }}
              onPointerDown={handleHuePointerDown}
              onPointerMove={handleHuePointerMove}
              onPointerUp={handleHuePointerUp}
            >
              <div
                className="absolute left-0 right-0 h-2 rounded-sm pointer-events-none"
                style={{
                  top: `${(hsb.h / 360) * 100}%`,
                  transform: 'translateY(-50%)',
                  border: '2px solid white',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                }}
              />
            </div>
          </div>

          {/* ── Alpha Slider ── */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-gray-500">Opacity</Label>
              <span className="text-xs text-gray-500 tabular-nums">{alpha}%</span>
            </div>
            <div
              ref={alphaRef}
              className="relative h-3 rounded-full cursor-pointer overflow-hidden"
              style={{ backgroundImage: CHECKER_SVG, backgroundSize: '16px 16px' }}
              onPointerDown={handleAlphaPointerDown}
              onPointerMove={handleAlphaPointerMove}
              onPointerUp={handleAlphaPointerUp}
            >
              <div
                className="absolute inset-0 rounded-full"
                style={{ background: alphaGradient }}
              />
              <div
                className="absolute top-1/2 w-3.5 h-3.5 rounded-full border-2 border-white pointer-events-none"
                style={{
                  left: `${alpha}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.15)',
                  backgroundColor: hex,
                }}
              />
            </div>
          </div>

          {/* ── Color Preview + Inputs ── */}
          <div className="flex gap-3 items-start">
            {/* Preview swatch */}
            <div className="flex flex-col items-center gap-1">
              <div
                className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200"
                style={{ backgroundImage: CHECKER_SVG, backgroundSize: '8px 8px' }}
              >
                <div
                  className="w-full h-full"
                  style={{ backgroundColor: outputColor }}
                />
              </div>
              <span className="text-[10px] text-gray-400">Preview</span>
            </div>

            {/* Color inputs */}
            <div className="flex-1 space-y-2">
              {/* Mode selector tabs */}
              <div className="flex gap-0.5 bg-gray-100 rounded-md p-0.5">
                {(['hex', 'rgb', 'hsl'] as const).map(mode => (
                  <button
                    key={mode}
                    onClick={() => setInputMode(mode)}
                    className={`flex-1 text-[11px] font-medium py-1 rounded transition-colors ${
                      inputMode === mode
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {mode.toUpperCase()}
                  </button>
                ))}
              </div>

              {inputMode === 'hex' && (
                <div className="flex gap-2 items-center">
                  <Input
                    value={hexInput}
                    onChange={(e) => handleHexChange(e.target.value)}
                    onFocus={() => { hexInputFocused.current = true; }}
                    onBlur={() => { hexInputFocused.current = false; }}
                    placeholder="#000000"
                    className="font-mono text-sm h-8"
                    maxLength={7}
                  />
                </div>
              )}

              {inputMode === 'rgb' && (
                <div className="flex gap-1.5">
                  {(['r', 'g', 'b'] as const).map(ch => (
                    <div key={ch} className="flex-1">
                      <Label className="text-[10px] text-gray-400 uppercase">{ch}</Label>
                      <Input
                        type="number"
                        min={0}
                        max={255}
                        value={rgb[ch]}
                        onChange={(e) => handleRgbChange(ch, parseInt(e.target.value))}
                        className="font-mono text-sm h-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  ))}
                </div>
              )}

              {inputMode === 'hsl' && (
                <div className="flex gap-1.5">
                  {(['h', 's', 'l'] as const).map(ch => (
                    <div key={ch} className="flex-1">
                      <Label className="text-[10px] text-gray-400 uppercase">
                        {ch}{ch === 'h' ? '\u00B0' : '%'}
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        max={ch === 'h' ? 360 : 100}
                        value={hsl[ch]}
                        onChange={(e) => handleHslChange(ch, parseInt(e.target.value))}
                        className="font-mono text-sm h-8 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Preset Colors ── */}
          <div>
            <Label className="text-xs text-gray-500 mb-1.5 block">Presets</Label>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => handleSwatchClick(color)}
                  className="w-6 h-6 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500"
                  style={{
                    backgroundColor: color,
                    border: color === '#FFFFFF' ? '1px solid #D1D5DB' : '1px solid transparent',
                    boxShadow: hex === color ? '0 0 0 2px #2F855A' : undefined,
                  }}
                  title={color}
                />
              ))}
            </div>
          </div>

          {/* ── Recent Colors ── */}
          {recentColors.length > 0 && (
            <div>
              <Label className="text-xs text-gray-500 mb-1.5 block">Recent</Label>
              <div className="flex flex-wrap gap-1.5">
                {recentColors.map((color, i) => (
                  <button
                    key={`${color}-${i}`}
                    onClick={() => handleSwatchClick(color)}
                    className="w-6 h-6 rounded-md transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500"
                    style={{
                      backgroundColor: color,
                      border: color === '#FFFFFF' ? '1px solid #D1D5DB' : '1px solid transparent',
                      boxShadow: hex === color ? '0 0 0 2px #2F855A' : undefined,
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <DialogFooter className="px-5 py-3 border-t border-gray-100 flex-row justify-between items-center">
          <div>
            {supportsEyeDropper && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleEyeDropper}
                className="gap-1.5 text-xs"
              >
                <Pipette className="w-3.5 h-3.5" />
                Eyedropper
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleApply}>
              Apply Color
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
